//macadamia-app/src/macadamia-core/components/postits/PostItVisibilityPicker.tsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Globe from '../../icons/Globe';
import LockClosed from '../../icons/LockClosed';
import UserGroup from '../../icons/UserGroup';

type PostItVisibility = 'private' | 'shared' | 'public';

interface PostItVisibilityPickerProps {
  currentVisibility: PostItVisibility;
  onVisibilityChange: (visibility: PostItVisibility) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

const VISIBILITY_OPTIONS = [
  {
    value: 'private' as const,
    label: 'Seulement moi',
    icon: LockClosed,
    bgColor: 'hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-500',
    description: 'Visible uniquement par vous'
  },
  {
    value: 'shared' as const,
    label: 'Auteurs du dossier',
    icon: UserGroup,
    bgColor: 'hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-500',
    description: 'Visible par les auteurs et co-auteurs'
  },
  {
    value: 'public' as const,
    label: 'Tout le monde',
    icon: Globe,
    bgColor: 'hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-500',
    description: 'Visible par tous ceux qui ont accès'
  }
];

const PostItVisibilityPicker: React.FC<PostItVisibilityPickerProps> = ({
  currentVisibility,
  onVisibilityChange,
  onClose,
  position
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  // Fermer au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Ajouter le listener après un petit délai pour éviter la fermeture immédiate
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleVisibilityClick = (visibility: PostItVisibility) => {
    onVisibilityChange(visibility);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={pickerRef}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="absolute bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-lg rounded-lg p-2 z-50 min-w-[220px]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1">
          {VISIBILITY_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = option.value === currentVisibility;

            return (
              <button
                key={option.value}
                onClick={() => handleVisibilityClick(option.value)}
                onMouseDown={(e) => e.stopPropagation()}
                className={`
                  flex items-start gap-3 p-2 rounded-md transition-colors text-left
                  ${option.bgColor}
                  ${isActive ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-300' : ''}
                `}
                title={option.description}
                aria-label={option.label}
              >
                <Icon
                  width="20px"
                  height="20px"
                  fill="currentColor"
                  className="mt-0.5 flex-shrink-0 text-mcdm-third-400 dark:text-mcdm-primary-100"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-mcdm-third-400 dark:text-mcdm-primary-200">
                      {option.label}
                    </span>
                    {isActive && (
                      <span className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-100">
                        
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PostItVisibilityPicker;
